import React, { useEffect, useMemo, useState } from "react";
import { FormControlLabel, Switch } from "@mui/material";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";
import { DEFAULT_GRANT_TYPES, GRANT_TYPE_OPTIONS } from "../../constants";

const schema = {
  name: [required("Client name is required.")],
};

const ClientFormDialog = ({
  open,
  mode = "create",
  client = null,
  roles = [],
  rolesLoading = false,
  loading = false,
  showExtendedFields = false,
  onClose,
  onSubmit,
}) => {
  const isEdit = mode === "edit";
  const [formError, setFormError] = useState("");
  const [grantType, setGrantType] = useState(DEFAULT_GRANT_TYPES);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [redirectUri, setRedirectUri] = useState([]);
  const [allowUserCreation, setAllowUserCreation] = useState(!showExtendedFields);
  const [allowSelfUserCreation, setAllowSelfUserCreation] = useState(false);
  const [allowLoginViaOTP, setAllowLoginViaOTP] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState("");

  const { values, getFieldProps, validateAll, reset } = useFormValidation(
    { name: "" },
    schema
  );

  useEffect(() => {
    if (!open) return;
    setFormError("");

    if (isEdit && client) {
      reset({ name: client.name || "" });
      setGrantType(
        Array.isArray(client.grantType) && client.grantType.length
          ? client.grantType.map(String)
          : DEFAULT_GRANT_TYPES
      );
      setSelectedRoles(
        Array.isArray(client.roles) ? client.roles.map(String).filter(Boolean) : []
      );
      setRedirectUri(
        Array.isArray(client.redirectUri)
          ? client.redirectUri.map(String).filter(Boolean)
          : []
      );
      setAllowUserCreation(Boolean(client.allowUserCreation));
      setAllowSelfUserCreation(Boolean(client.allowSelfUserCreation));
      setAllowLoginViaOTP(Boolean(client.allowLoginViaOTP));
      setPrivacyPolicy(String(client.privacyPolicy || ""));
      return;
    }

    reset({ name: "" });
    setGrantType(DEFAULT_GRANT_TYPES);
    setSelectedRoles([]);
    setRedirectUri([]);
    setAllowUserCreation(!showExtendedFields);
    setAllowSelfUserCreation(false);
    setAllowLoginViaOTP(false);
    setPrivacyPolicy("");
  }, [client, isEdit, open, reset, showExtendedFields]);

  const roleOptions = useMemo(() => {
    const fromApi = (roles || []).map((role) =>
      typeof role === "string" ? role : role?.name || role?.role || String(role)
    );
    return Array.from(new Set([...fromApi, ...selectedRoles].filter(Boolean)));
  }, [roles, selectedRoles]);

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setFormError(errors.name || "Please correct the highlighted fields.");
      return;
    }
    if (!grantType.length) {
      setFormError("Select at least one grant type.");
      return;
    }
    if (!selectedRoles.length) {
      setFormError("Select at least one role.");
      return;
    }

    setFormError("");
    try {
      const cleanedRedirectUris = (redirectUri || [])
        .map((uri) => String(uri || "").trim())
        .filter(Boolean);
      const cleanedPrivacyPolicy = privacyPolicy.trim();

      await onSubmit({
        name: values.name.trim(),
        grantType,
        allowUserCreation,
        allowSelfUserCreation,
        allowLoginViaOTP,
        roles: selectedRoles,
        ...(showExtendedFields
          ? {
              redirectUri: cleanedRedirectUris,
              ...(cleanedPrivacyPolicy
                ? { privacyPolicy: cleanedPrivacyPolicy }
                : {}),
            }
          : {}),
      });
    } catch (err) {
      setFormError(
        err?.message ||
          (isEdit ? "Unable to update client." : "Unable to create client.")
      );
    }
  };

  const canSubmit = Boolean(
    values.name?.trim() &&
      grantType.length > 0 &&
      selectedRoles.length > 0 &&
      !loading
  );

  return (
    <FormDialog
      open={open}
      title={isEdit ? "Edit Client" : "Add Client"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      disableSubmit={!canSubmit}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isEdit ? "Save Changes" : "Create Client"}
      loadingLabel={isEdit ? "Saving..." : "Creating..."}
    >
      <FormField label="Client Name" required {...getFieldProps("name")} />
      <ChipAutocomplete
        label="Grant Type"
        required
        options={GRANT_TYPE_OPTIONS}
        value={grantType}
        onChange={setGrantType}
        disabled={loading}
      />
      <ChipAutocomplete
        label="Roles"
        required
        freeSolo
        options={roleOptions}
        value={selectedRoles}
        onChange={setSelectedRoles}
        loading={rolesLoading}
        disabled={loading}
      />
      {showExtendedFields ? (
        <ChipAutocomplete
          label="Redirect URIs"
          freeSolo
          options={redirectUri}
          value={redirectUri}
          onChange={setRedirectUri}
          disabled={loading}
          placeholder="https://example.com/callback"
          helperText="Optional. OAuth callback URLs for this client."
        />
      ) : null}
      <FormControlLabel
        sx={{ ml: 0, alignItems: "center" }}
        control={
          <Switch
            checked={allowUserCreation}
            onChange={(event) => setAllowUserCreation(event.target.checked)}
            disabled={loading}
          />
        }
        label="Allow user creation"
      />
      {showExtendedFields ? (
        <>
          <FormControlLabel
            sx={{ ml: 0, alignItems: "center" }}
            control={
              <Switch
                checked={allowSelfUserCreation}
                onChange={(event) =>
                  setAllowSelfUserCreation(event.target.checked)
                }
                disabled={loading}
              />
            }
            label="Allow self user creation"
          />
          <FormControlLabel
            sx={{ ml: 0, alignItems: "center" }}
            control={
              <Switch
                checked={allowLoginViaOTP}
                onChange={(event) => setAllowLoginViaOTP(event.target.checked)}
                disabled={loading}
              />
            }
            label="Allow login via OTP"
          />
          {isEdit ? (
            <FormField
              label="Privacy Policy"
              value={privacyPolicy}
              onChange={(event) => setPrivacyPolicy(event.target.value)}
              placeholder="https://example.com/privacy-policy"
              disabled={loading}
              helperText="Optional. Leave blank if not needed."
            />
          ) : null}
        </>
      ) : null}
    </FormDialog>
  );
};

export default ClientFormDialog;
